<template>
  <div class="ai-agent">
    <div class="head" v-if="!embedded">
      <h1>🤖 AI Call-Center Agent
        <span class="stamp" title="Deployed build marker">build 2026-09-11 · v1</span>
      </h1>
      <p class="sub">How the AI agent performed — orders it confirmed for you, cash collected, and fees.</p>
      <button class="refresh" @click="load" :disabled="loading">{{ loading ? 'Loading…' : '↻ Refresh' }}</button>
    </div>

    <div v-if="isAdmin !== true || (loading && !data)" class="loading">
      <span class="spinner"></span> Loading…
    </div>

    <div v-else-if="data">
      <!-- toolbar: date range + refresh -->
      <div class="ai-toolbar">
        <div class="ai-range">
          <button v-for="q in quickRanges" :key="q.days" :class="{ on: activeQuick === q.days }" @click="quickRange(q.days)">{{ q.label }}</button>
        </div>
        <button class="ai-refresh" @click="load" :disabled="loading">
          <span class="ai-refresh-ic" :class="{ spin: loading }">↻</span> {{ loading ? 'Refreshing' : 'Refresh' }}
        </button>
      </div>

      <!-- HERO: agent console band -->
      <div class="ai-console" :class="{ idle: !data.processed }">
        <div class="ai-console-grid"></div>
        <div class="ai-console-head">
          <div class="ai-bot">
            <span class="ai-bot-face">🤖</span>
            <span class="ai-bot-pulse"></span>
          </div>
          <div class="ai-bot-id">
            <span class="ai-bot-name">Agent AI</span>
            <span class="ai-bot-status"><i></i> Working autonomously</span>
          </div>
        </div>

        <div class="ai-console-story" v-if="data.processed > 0">
          Confirmed <b>{{ data.confirmed.toLocaleString() }}</b> orders,
          collected <b class="pos">${{ data.codCollected.toLocaleString() }}</b> in cash,
          for <b class="neg">${{ data.feesTotal.toLocaleString() }}</b> in fees.
        </div>
        <div class="ai-console-story" v-else>
          Confirmed <b>{{ data.confirmed.toLocaleString() }}</b> orders — none have reached
          <b>Processed</b> (cash) yet, so no fees are due.
        </div>

        <div class="ai-console-roi" v-if="data.processed > 0 && data.feesTotal > 0">
          <span class="ai-roi-badge">{{ Math.round(data.codCollected / data.feesTotal) }}×</span>
          <span>return on spend — the AI earned {{ Math.round(data.codCollected / data.feesTotal) }}× what it cost.</span>
        </div>
      </div>

      <!-- HEADLINE — the whole story in one sentence -->
      <div class="ai-lede">
        <!-- Line 1: the work done — calls the team did not have to make. -->
        <p class="ai-lede-main">
          The AI handled <b>{{ data.confirmed.toLocaleString() }}</b>
          {{ data.confirmed === 1 ? 'order' : 'orders' }} without a call<template v-if="data.valueTotal">,
          worth <b>{{ data.valueTotal.toLocaleString() }}</b> in orders</template>.
          <template v-if="data.gate && data.gate.blocked">
            <b>{{ data.gate.blocked.toLocaleString() }}</b> more had no address at all and were left for a human.
          </template>
        </p>

        <!-- Line 2: where those orders actually stand. The old copy quoted
             precision alone, which reads as "wrong 62% of the time" when most
             orders simply have not finished yet. Say what is still moving. -->
        <p class="ai-lede-sub">
          <template v-if="settledCount">
            Of the <b>{{ settledCount.toLocaleString() }}</b>
            that {{ settledCount === 1 ? 'has' : 'have' }} finished,
            <b>{{ data.processed.toLocaleString() }}</b> reached cash<template v-if="data.precision != null">
            — <b>{{ data.precision }}%</b></template>.
            <template v-if="data.pending">
              The other <b>{{ data.pending.toLocaleString() }}</b> are still in transit, so the rate can still move.
            </template>
          </template>
          <template v-else-if="data.confirmed">
            None have finished delivery yet — every one is still in transit, so there is no success rate to report.
          </template>
        </p>

        <!-- Line 3: the quieter supporting detail. -->
        <p class="ai-lede-sub">
          <template v-if="data.confidence">
            The AI only acted when it was sure: median confidence <b>{{ data.confidence.median }}%</b><template
              v-if="data.confidence.min !== data.confidence.max">
            (range {{ data.confidence.min }}–{{ data.confidence.max }}%)</template>.
          </template>
          <template v-if="data.address && data.address.enriched">
            <b>{{ data.address.enriched.toLocaleString() }}</b>
            {{ data.address.enriched === 1 ? 'address was' : 'addresses were' }} recovered from past deliveries.
          </template>
          <template v-if="data.processed">
            Fees so far <b>${{ money(data.feesTotal) }}</b>
            ({{ data.processed.toLocaleString() }} × ${{ money(data.pricePerProcessed) }}), report-only — you are billed only on cash collected.
          </template>
          <template v-else>
            No fees yet — billing starts only when an order reaches cash.
          </template>
        </p>
      </div>

      <!-- STAT CARDS — only the four that matter -->
      <div class="ai-cards">
        <ai-stat-card icon="✅" tone="primary" label="Orders confirmed" :value="data.confirmed"  note="by the AI agent — calls saved" />
        <ai-stat-card icon="💵" tone="green"  label="Processed"        :value="data.processed"  note="reached cash — billable" />
        <!-- the unbilled middle: shipped but not yet paid, so neither win nor loss.
             neutral tone on purpose — "blue" renders red in AiStatCard. -->
        <ai-stat-card icon="🚚" tone="neutral" label="Still in flight" :value="data.pending"
          note="shipped, cash not collected yet — $0 so far" />
        <ai-stat-card icon="✕"  tone="red"    label="Failed"           :value="data.failed"     note="cancelled, refused or returned — $0 charged" />
        <ai-stat-card icon="🧾" tone="amber"  label="Total fees" prefix="$" :value="data.feesTotal"
          :note="`${data.processed} × $${data.pricePerProcessed} · report only`" />
      </div>

      <!-- PRECISION METER -->
      <div class="ai-meter" v-if="data.precision != null">
        <div class="ai-meter-head">
          <span class="ai-meter-label">Confirmation precision</span>
          <span class="ai-meter-pct">{{ data.precision }}%</span>
        </div>
        <div class="ai-meter-bar">
          <div class="ai-meter-fill" :style="{ width: data.precision + '%' }"></div>
        </div>
        <div class="ai-meter-foot">
          <b>{{ (data.processed + data.failed).toLocaleString() }}</b> confirmed orders reached a final outcome —
          <b>{{ data.processed.toLocaleString() }}</b> Processed.
          <span class="dim">Fees are report-only; no charge is written yet.</span>
        </div>
      </div>

      <!-- ORDER VALUE — one compact line, only once there is something to say -->
      <div class="ai-value" v-if="data.valueTotal">
        <span><b>{{ data.valueTotal.toLocaleString() }}</b> total order value confirmed</span>
        <span class="ai-value-sep">·</span>
        <span><b>{{ data.valueAvg.toLocaleString() }}</b> average</span>
        <span class="ai-value-sep">·</span>
        <span><b>{{ data.codCollected.toLocaleString() }}</b> collected so far</span>
      </div>

      <!-- PER-SELLER -->
      <div class="ai-section" v-if="allSellerRows.length">
        <div class="ai-section-head">
          <h2>By seller</h2>
          <span class="ai-section-count">{{ filteredSellerRows.length }} / {{ allSellerRows.length }} {{ allSellerRows.length === 1 ? 'seller' : 'sellers' }}</span>
          <input v-model="sellerSearch" class="ai-search" type="search" placeholder="Search a seller…" />
        </div>
        <div class="ai-table-wrap">
          <table class="ai-tbl">
            <thead><tr>
              <th>Seller</th>
              <th class="ta-r">Confirmed</th>
              <th class="ta-c">Address quality</th>
              <th class="ta-r">Order value</th>
              <th class="ta-r">Avg conf.</th>
              <th class="ta-r">In flight</th>
              <th class="ta-r">Processed</th>
              <th class="ta-r">Fees</th>
              <th class="ta-c"></th>
            </tr></thead>
            <tbody>
              <tr v-for="r in perSellerRows" :key="r.seller">
                <td>
                  <span class="ai-seller">
                    <span class="ai-seller-dot">{{ (r.name || '?').slice(0,1).toUpperCase() }}</span>
                    <span class="ai-seller-col">
                      <span class="ai-seller-name">{{ r.name }}</span>
                      <!-- share of all AI confirmations, so the big stores stand out -->
                      <span class="ai-share"><i :style="{ width: sellerShare(r) + '%' }"></i></span>
                    </span>
                  </span>
                </td>
                <td class="ta-r ai-num"><b>{{ r.confirmed.toLocaleString() }}</b></td>
                <!-- address quality: the one thing that really varies per store -->
                <td class="ta-c">
                  <span v-if="r.addrRate != null" class="ai-addr-pill" :class="addrRateTone(r.addrRate)">
                    {{ r.addrRate }}%
                  </span>
                  <span v-else class="ai-zero">—</span>
                  <span class="ai-addr-sub" v-if="r.addrRate != null">
                    {{ r.addrOk }}/{{ r.confirmed }} usable<template v-if="r.enriched"> · {{ r.enriched }} filled</template>
                  </span>
                </td>
                <td class="ta-r ai-num">{{ (r.value || 0).toLocaleString() }}</td>
                <td class="ta-r ai-num" :class="{ 'ai-zero': r.avgScore == null }">{{ r.avgScore != null ? r.avgScore + '%' : '—' }}</td>
                <td class="ta-r ai-num" :class="{ 'ai-zero': !sellerPending(r) }">{{ sellerPending(r).toLocaleString() }}</td>
                <td class="ta-r ai-num" :class="{ 'ai-zero': !r.processed }">{{ r.processed.toLocaleString() }}</td>
                <td class="ta-r ai-fee-cell" :class="{ 'ai-zero': !r.fees }">${{ r.fees.toLocaleString() }}</td>
                <td class="ta-c">
                  <button type="button" class="ai-eye" :title="`See ${r.name}'s orders`" @click="openSellerOrders(r)">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"
                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="!perSellerRows.length">
                <td colspan="9" class="ai-empty-row">No seller matches “{{ sellerSearch }}”.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- pagination (only when more than one page of sellers) -->
        <div class="ai-pager" v-if="sellerTotalPages > 1">
          <button class="ai-pager-btn" :disabled="sellerPage === 1" @click="sellerPrev">‹ Précédent</button>
          <span class="ai-pager-info">Page <b>{{ sellerPage }}</b> / {{ sellerTotalPages }}
            <span class="ai-pager-dim">· {{ allSellerRows.length }} vendeurs</span></span>
          <button class="ai-pager-btn" :disabled="sellerPage === sellerTotalPages" @click="sellerNext">Suivant ›</button>
        </div>
      </div>
    </div>

    <div v-else class="loading">No data yet.</div>

    <!-- one seller's orders, opened from the eye button in the by-seller table -->
    <vue-modal-2 name="aiSellerOrders" :headerOptions="{ title: ' ' }" :noFooter="true" modalSize="xl"
                 @on-close="$vm2.close('aiSellerOrders')">
      <div class="ai-modal">
        <div class="ai-modal-head">
          <span class="ai-seller-dot lg">{{ (modalSeller.name || '?').slice(0,1).toUpperCase() }}</span>
          <div class="ai-modal-title">
            <h3>{{ modalSeller.name }}</h3>
            <p>
              <b>{{ modalSellerOrders.length }}</b><template v-if="modalSellerOrders.length !== modalSellerAll.length"> / {{ modalSellerAll.length }}</template>
              {{ modalSellerAll.length === 1 ? 'order' : 'orders' }} confirmed by the AI
              <template v-if="modalSeller.addrRate != null"> · <b>{{ modalSeller.addrRate }}%</b> with a real address</template>
              <template v-if="modalSeller.value"> · <b>{{ modalSeller.value.toLocaleString() }}</b> total value</template>
              <br />
              <b>{{ outcomeCount('processed') }}</b> processed ·
              <b>{{ outcomeCount('pending') }}</b> still in flight ·
              <b>{{ outcomeCount('failed') }}</b> failed
            </p>
          </div>
          <!-- outcome filter: settled vs still in flight -->
          <div class="ai-dd" :class="{ open: outDdOpen }">
            <button type="button" class="ai-dd-btn" @click="outDdOpen = !outDdOpen; shipDdOpen = false">
              <span v-if="modalOutcome" class="ai-status-pill sm" :class="outcomeTone(modalOutcome)">{{ outcomeLabel(modalOutcome) }}</span>
              <span v-else class="ai-dd-any">Any outcome</span>
              <svg class="ai-dd-caret" viewBox="0 0 24 24" width="14" height="14" fill="none"
                   stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div class="ai-dd-back" v-if="outDdOpen" @click="outDdOpen = false"></div>
            <div class="ai-dd-menu" v-if="outDdOpen">
              <button type="button" class="ai-dd-item" :class="{ sel: modalOutcome === '' }"
                      @click="modalOutcome = ''; outDdOpen = false">
                <span class="ai-dd-any">Any outcome</span>
                <span class="ai-dd-n">{{ modalSellerAll.length }}</span>
              </button>
              <button type="button" v-for="o in ['processed','pending','failed']" :key="o" class="ai-dd-item"
                      :class="{ sel: modalOutcome === o }" @click="modalOutcome = o; outDdOpen = false">
                <span class="ai-status-pill sm" :class="outcomeTone(o)">{{ outcomeLabel(o) }}</span>
                <span class="ai-dd-n">{{ outcomeCount(o) }}</span>
              </button>
            </div>
          </div>
          <!-- custom dropdown (the native select does not match the rest of the UI) -->
          <div class="ai-dd" :class="{ open: shipDdOpen }">
            <button type="button" class="ai-dd-btn" @click="shipDdOpen = !shipDdOpen; outDdOpen = false">
              <span v-if="modalShip" class="ai-status-pill sm" :class="shipTone(modalShip)">{{ modalShip }}</span>
              <span v-else class="ai-dd-any">Any shipping status</span>
              <svg class="ai-dd-caret" viewBox="0 0 24 24" width="14" height="14" fill="none"
                   stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <!-- invisible backdrop: one click anywhere else closes the menu -->
            <div class="ai-dd-back" v-if="shipDdOpen" @click="shipDdOpen = false"></div>
            <div class="ai-dd-menu" v-if="shipDdOpen">
              <button type="button" class="ai-dd-item" :class="{ sel: modalShip === '' }"
                      @click="modalShip = ''; shipDdOpen = false">
                <span class="ai-dd-any">Any shipping status</span>
                <span class="ai-dd-n">{{ modalSellerAll.length }}</span>
              </button>
              <button type="button" v-for="s in modalShipOptions" :key="s" class="ai-dd-item"
                      :class="{ sel: modalShip === s }" @click="modalShip = s; shipDdOpen = false">
                <span class="ai-status-pill sm" :class="shipTone(s)">{{ s }}</span>
                <span class="ai-dd-n">{{ shipCount(s) }}</span>
              </button>
            </div>
          </div>
          <input v-model="modalSearch" class="ai-search" type="search" placeholder="Order ID, tracking, phone…" />
        </div>

        <div class="ai-table-wrap ai-scroll ai-modal-scroll">
          <table class="ai-tbl ai-tbl-orders">
            <thead><tr>
              <th>Date</th><th>Order</th><th>Address</th><th>Tracking</th><th>Status</th><th>Shipping</th>
              <th>Outcome</th><th>Comment</th><th class="ta-r">Conf.</th><th class="ta-r">Value</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in modalSellerOrders" :key="r._id">
                <td class="ai-dim nowrap">{{ shortDate(r.createdAt) }}</td>
                <td>
                  <div class="ai-ord-ref">{{ r.orderRef || '—' }}</div>
                  <div class="ai-dim sm">{{ r.customer }}<template v-if="r.phone"> · {{ r.phone }}</template></div>
                </td>
                <td>
                  <span v-if="addressClass(r) === 'full'" class="ai-addr ok" :title="r.address">{{ r.address }}</span>
                  <span v-else-if="addressClass(r) === 'city'" class="ai-addr city"
                        :title="r.address + ' — city only: shippable, the courier calls on arrival'">{{ r.address }} · city only</span>
                  <span v-else class="ai-addr bad" :title="r.address || 'no address'">{{ r.address || '—' }} · no address</span>
                </td>
                <td>
                  <span v-if="r.trackingNumber" class="ai-track">{{ r.trackingNumber }}</span>
                  <span v-else class="ai-zero">—</span>
                </td>
                <td><span class="ai-status-pill" :class="statusTone(r)">{{ r.status || '—' }}</span></td>
                <td>
                  <span v-if="r.shipStatus" class="ai-status-pill" :class="shipTone(r.shipStatus)">{{ r.shipStatus }}</span>
                  <span v-else class="ai-zero">—</span>
                </td>
                <!-- settled or still in flight: the money question the status
                     columns alone don't answer (shipped = not paid yet) -->
                <td>
                  <span class="ai-status-pill" :class="outcomeTone(r.outcome)">{{ outcomeLabel(r.outcome) }}</span>
                </td>
                <!-- the human's note: usually WHY it failed ("Rupture"). The status
                     pills say an order failed; only this says what went wrong. -->
                <td>
                  <span v-if="r.comment" class="ai-comment" :title="r.comment">{{ r.comment }}</span>
                  <span v-else class="ai-zero">—</span>
                </td>
                <td class="ta-r ai-num" :class="{ 'ai-zero': r.score == null }">{{ r.score != null ? r.score + '%' : '—' }}</td>
                <td class="ta-r ai-num">{{ (r.totalPrice || 0).toLocaleString() }}</td>
              </tr>
              <tr v-if="!modalSellerOrders.length">
                <td colspan="10" class="ai-empty-row">No order matches this search.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
import AiStatCard from '../../components/ui/AiStatCard.vue';

const ADMIN_TYPES = ['Admin', 'Administrateur', 'CEO'];

export default {
  name: 'AiAgentAnalytics',
  components: { AiStatCard },
  props: {
    // when rendered INSIDE another (already admin-gated) page, skip the standalone
    // header + the redirect-on-non-admin behavior.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false, data: null, isAdmin: null,
      fromDate: '', toDate: '', activeQuick: 90,
      quickRanges: [
        { days: 7, label: 'Last 7 days' },
        { days: 30, label: 'Last 30 days' },
        { days: 90, label: 'Last 90 days' },
      ],
      sellerNames: {},
      sellerPage: 1,
      sellerPerPage: 8,
      sellerSearch: '',
      // one-seller popup (orders are viewed per seller, via the eye button)
      modalSeller: {}, modalSearch: '', modalShip: '', shipDdOpen: false,
      modalOutcome: '', outDdOpen: false,
    };
  },
  computed: {
    warhouseSelected() {
      try { return this.$store.getters["wharhouse/warhouseSelected"]({ user: {} }); }
      catch (e) { return null; }
    },
    // full, sorted list (used for the count + pagination math)
    allSellerRows() {
      if (!this.data || !this.data.perSeller) return [];
      return Object.entries(this.data.perSeller)
        .map(([seller, v]) => ({
          seller, name: this.sellerNames[seller] || seller.slice(-6),
          confirmed: v.confirmed, processed: v.processed, fees: v.fees,
          // address quality + exposure per store
          addrOk: v.addrOk, addrRate: v.addrRate, enriched: v.enriched,
          value: v.value, avgScore: v.avgScore,
        }))
        .sort((a, b) => b.confirmed - a.confirmed);
    },
    // search filter on the seller name (client-side — the list is already loaded)
    filteredSellerRows() {
      const q = (this.sellerSearch || '').toLowerCase().trim();
      if (!q) return this.allSellerRows;
      return this.allSellerRows.filter(r => (r.name || '').toLowerCase().includes(q));
    },
    sellerTotalPages() {
      return Math.max(1, Math.ceil(this.filteredSellerRows.length / this.sellerPerPage));
    },
    // the current page of sellers
    perSellerRows() {
      const start = (this.sellerPage - 1) * this.sellerPerPage;
      return this.filteredSellerRows.slice(start, start + this.sellerPerPage);
    },
    // ── one-seller popup ──
    // every order this seller has, before the popup's own search/status filter —
    // used both for the status dropdown and the header count.
    modalSellerAll() {
      if (!this.data || !this.data.rows || !this.modalSeller.seller) return [];
      const id = String(this.modalSeller.seller);
      return this.data.rows.filter(r => String(r.seller) === id);
    },
    // Orders that have actually finished (cash collected or definitively lost).
    // precision is computed over THESE, not over everything confirmed — quoting it
    // without saying so made a healthy agent look like it failed most of the time.
    settledCount() {
      if (!this.data) return 0;
      return (this.data.processed || 0) + (this.data.failed || 0);
    },
    // only the SHIPPING statuses this seller actually has, so the dropdown never
    // offers an option that returns nothing.
    modalShipOptions() {
      return [...new Set(this.modalSellerAll.map(r => r.shipStatus).filter(Boolean))].sort();
    },
    // the popup's rows: this seller's orders, narrowed by its search + shipping
    // status. No request needed — data.rows is already loaded for the page.
    modalSellerOrders() {
      const q = (this.modalSearch || '').toLowerCase().trim();
      return this.modalSellerAll.filter(r => {
        if (this.modalShip && r.shipStatus !== this.modalShip) return false;
        if (this.modalOutcome && r.outcome !== this.modalOutcome) return false;
        if (!q) return true;
        const hay = `${r.orderRef || ''} ${r.trackingNumber || ''} ${r.phone || ''} ${r.customer || ''} ${r.address || ''}`.toLowerCase();
        return hay.includes(q);
      });
    },
  },
  watch: {
    // searching changes the result count — jump back to page 1 so you never land
    // on a page that no longer exists.
    sellerSearch() { this.sellerPage = 1; },
  },
  methods: {
    // open the per-seller popup (clears any previous search first)
    openSellerOrders(r) {
      this.modalSeller = r;
      this.modalSearch = '';
      this.modalShip = '';
      this.shipDdOpen = false;
      this.modalOutcome = '';
      this.outDdOpen = false;
      this.$vm2.open('aiSellerOrders');
    },
    // how many of this seller's orders sit at that shipping status — shown next to
    // each option so you know what you get before you pick it.
    shipCount(s) {
      return this.modalSellerAll.filter(r => r.shipStatus === s).length;
    },
    outcomeCount(o) {
      return this.modalSellerAll.filter(r => r.outcome === o).length;
    },
    // money with cents, so the per-order price reads "$0.40" not "$0.4"
    money(v) {
      return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // per-seller "in flight" — derived from rows (which carry outcome) rather than
    // from perSeller, which has no pending counter. Falls back to confirmed minus
    // the two settled buckets when rows are absent, so the column never reads 0
    // just because the API build is older.
    sellerPending(r) {
      const rows = (this.data && this.data.rows) || null;
      if (rows && rows.length) {
        const id = String(r.seller);
        return rows.filter(x => String(x.seller) === id && x.outcome === 'pending').length;
      }
      return Math.max(0, (r.confirmed || 0) - (r.processed || 0) - (r.failed || 0));
    },
    // "pending" is the honest word for it: the parcel is out but the cash is not
    // in, so it is neither a win nor a loss yet — and it is not billed.
    outcomeLabel(o) {
      if (o === 'processed') return 'Processed';
      if (o === 'failed') return 'Failed';
      return 'Pending';
    },
    outcomeTone(o) {
      if (o === 'processed') return 'st-good';
      if (o === 'failed') return 'st-bad';
      return 'st-mid';
    },
    // short, readable date for the orders table
    shortDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (isNaN(dt)) return '—';
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) + ' ' +
             dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    },
    // Classify the address the way the GATE does, not by string length.
    // The old rule was `length >= 8`, which mislabelled real short cities
    // ("Nkayi", "Oyo", "Dolisie") as "no address" while letting a long
    // non-address ("Congo Brazzaville") read as fine. Three honest classes:
    //   full - precise enough to dispatch to (number / street / landmark)
    //   city - names a place; shippable, the courier phones on arrival
    //   none - nothing to deliver to; this is what the gate actually blocks
    addressClass(r) {
      const raw = (r.address == null ? '' : String(r.address)).replace(/\s+/g, ' ').trim();
      if (!raw) return 'none';
      if (/^[^a-zA-Z0-9]+$/.test(raw)) return 'none';
      const key = raw.toLowerCase()
        .normalize('NFKC').normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
      if (!key) return 'none';
      const JUNK = ['congo','cg','rc','rdc','cd','gabon','ga','default','defaultaddress',
        'defaultcity','na','null','none','test','aaa','xxx','unknown','inconnu','nc'];
      const FILLER = ['republique','du','de','la','le','les','et','a','au','aux','republic','of'];
      const rest = key.split(' ').filter(function (w) {
        return w && JUNK.indexOf(w) === -1 && FILLER.indexOf(w) === -1;
      });
      if (!rest.length) return 'none';
      const CITY = ['brazzaville','brazaville','braza','brazza','brizaville','pointe noire',
        'point noir','pointenoire','pnoire','pn','dolisie','nkayi','ouesso','impfondo','oyo',
        'owando','kinkala','sibiti','madingou','gamboma','mossendjo','kombo','nkombo','ouenze',
        'mpila','talangai','bacongo','makelekele','poto poto','moungali','centre ville',
        'centreville','ville','mayinga','magesje'];
      const restKey = rest.join(' ');
      if (CITY.indexOf(restKey) !== -1) return 'city';
      if (/[0-9]/.test(restKey)) return 'full';
      if (/(^| )(rue|avenue|av|bd|boulevard|quartier|carrefour|rond|face|derriere|pres|vers|arret|station|ecole|eglise|hopital|pharmacie|marche|villa|lot|km|route|cite|imm|immeuble|plateau|port)( |$)/.test(restKey)) return 'full';
      const meaningful = rest.filter(function (w) { return CITY.indexOf(w) === -1; });
      return meaningful.length ? 'full' : 'city';
    },
    // kept for the address-quality figures elsewhere on the page
    hasRealAddress(r) {
      return this.addressClass(r) === 'full';
    },
    // colour the shipping pill: delivered/paid is the win, a return or refusal is
    // the loss, everything else is still in flight.
    shipTone(s) {
      const v = String(s || '').toLowerCase();
      if (/processed|delivered|paid|received/.test(v)) return 'st-good';
      if (/refus|cancel|return|outofstock|unreachable|rembours|refund/.test(v)) return 'st-bad';
      return 'st-mid';
    },
    // colour the status pill by what the status means for the money
    statusTone(r) {
      const s = String(r.status || '').toLowerCase();
      if (r.outcome === 'processed' || /processed|delivered|paid/.test(s)) return 'st-good';
      if (r.outcome === 'failed' || /cancel|refus|return|spam|double|outofstock|unreach/.test(s)) return 'st-bad';
      return 'st-mid';
    },
    // colour the address-quality pill: a store sending mostly unusable addresses
    // is the one to go fix, so make that obvious.
    addrRateTone(rate) {
      if (rate >= 90) return 'rate-good';
      if (rate >= 60) return 'rate-mid';
      return 'rate-bad';
    },
    // share of the biggest seller, for the little bar under each name
    sellerShare(r) {
      const top = this.allSellerRows.length ? this.allSellerRows[0].confirmed : 0;
      if (!top) return 0;
      return Math.max(4, Math.round(100 * (r.confirmed || 0) / top));
    },
    quickRange(days) {
      this.activeQuick = days;
      const d = new Date(Date.now() - days * 864e5);
      this.fromDate = d.toISOString().slice(0, 10);
      this.toDate = '';
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        const payload = {};
        if (this.fromDate) payload.from = this.fromDate;
        if (this.toDate) payload.to = this.toDate;
        // filter the AI stats by the header-selected warehouse
        const _country = await this.warhouseSelected;
        if (_country) payload.country = _country;
        const raw = await this.$server.action('autoconfirm', 'aiAgentStats', payload);
        this.data = raw && raw.content ? raw.content : raw;
        this.sellerPage = 1;
        await this.loadSellerNames();
      } catch (e) {
        console.log('aiAgentStats load error', e && e.message);
      }
      this.loading = false;
    },
    // Resolve real seller names for the ids in perSeller (so the table shows names,
    // not id fragments). Fetches sellers for the current warehouse country and builds
    // an id -> name map. Best-effort — falls back to the id tail if a name is missing.
    async loadSellerNames() {
      try {
        const ids = this.data && this.data.perSeller ? Object.keys(this.data.perSeller) : [];
        if (!ids.some(id => id && id !== 'unknown' && !this.sellerNames[id])) return;
        const filter = { type: 'Seller', limit: 1000 };
        const country = await this.warhouseSelected;
        if (country) filter.countries = country;
        const res = await this.$server.search('users', filter);
        const list = (res && res.content && res.content.results) ? res.content.results : [];
        const map = { ...this.sellerNames };
        list.forEach(u => { if (u && u._id) map[String(u._id)] = u.fullName || u.company || u.email || String(u._id).slice(-6); });
        this.sellerNames = map;
      } catch (e) { /* keep id fallback */ }
    },
    sellerPrev() { if (this.sellerPage > 1) this.sellerPage--; },
    sellerNext() { if (this.sellerPage < this.sellerTotalPages) this.sellerPage++; },
  },
  async mounted() {
    // When embedded, the parent page already gated admin access — just load.
    if (this.embedded) { this.isAdmin = true; this.quickRange(90); return; }
    try {
      const me = await this.$server.me('users');
      const user = me && me.content ? me.content : me;
      this.isAdmin = !!(user && ADMIN_TYPES.indexOf(user.type) !== -1);
      if (!this.isAdmin) { window.location.href = '/'; return; }
    } catch (e) { this.isAdmin = false; window.location.href = '/'; return; }
    this.quickRange(90);
  },
};
</script>

<style scoped>
.ai-agent {
  --bg: #fbf9f9; --ink: #241a1a; --ink-soft: #6b5b5b; --muted: #9a8a8a;
  --card: #fff; --card-bd: #f0e7e7; --track: #f7efef;
  --primary: #e11d2a; --primary-2: #b81420;
  --pos: #059669; --neg: #d97706;
  color: var(--ink);
  font-feature-settings: "tnum" 1;
}

.head h1 { font-size: 22px; font-weight: 800; margin: 0; letter-spacing: -.01em; }
.stamp { font-size: 11px; font-weight: 600; color: var(--muted); background: var(--track); border: 1px solid var(--card-bd); padding: 2px 8px; border-radius: 20px; vertical-align: middle; margin-left: 8px; }
.head .sub { color: var(--ink-soft); font-size: 13px; margin: 4px 0 14px; }
.refresh { background: var(--primary); color: #fff; border: 0; padding: 7px 14px; border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 600; }
.loading { padding: 44px; text-align: center; color: var(--muted); }
.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid #f5cdd0; border-top-color: var(--primary); border-radius: 50%; animation: spin .8s linear infinite; vertical-align: middle; }
@keyframes spin { to { transform: rotate(360deg); } }

/* toolbar */
.ai-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
.ai-range { display: inline-flex; gap: 3px; background: var(--track); border-radius: 11px; padding: 4px; }
.ai-range button { background: none; border: 0; color: var(--ink-soft); padding: 6px 14px; border-radius: 8px; cursor: pointer; font-size: 12.5px; font-weight: 600; transition: all .16s ease; }
.ai-range button:hover { color: var(--ink); }
.ai-range button.on { background: var(--card); color: var(--primary); box-shadow: 0 1px 3px rgba(36,26,26,.12); }
.ai-refresh { display: inline-flex; align-items: center; gap: 6px; background: var(--card); border: 1px solid var(--card-bd); color: var(--ink-soft); padding: 7px 14px; border-radius: 9px; cursor: pointer; font-size: 12.5px; font-weight: 600; }
.ai-refresh:hover { border-color: var(--primary); color: var(--primary); }
.ai-refresh-ic { display: inline-block; }
.ai-refresh-ic.spin { animation: spin .8s linear infinite; }

/* ── agent console hero ── */
.ai-console {
  position: relative; overflow: hidden; border-radius: 18px; padding: 22px 24px; margin-bottom: 20px;
  background: radial-gradient(120% 140% at 0% 0%, #7a1018 0%, #4a0d12 55%, #380a0e 100%);
  color: #fbe7e8; box-shadow: 0 18px 40px -20px rgba(185, 20, 32, .5);
}
.ai-console-grid {
  position: absolute; inset: 0; pointer-events: none; opacity: .5;
  background-image: linear-gradient(rgba(250,150,155,.08) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(250,150,155,.08) 1px, transparent 1px);
  background-size: 26px 26px; mask-image: linear-gradient(115deg, #000, transparent 70%);
}
.ai-console-head { position: relative; display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.ai-bot { position: relative; width: 46px; height: 46px; border-radius: 14px; flex-shrink: 0;
  background: linear-gradient(150deg, #ef2b38, #a81119); display: flex; align-items: center; justify-content: center;
  font-size: 22px; box-shadow: 0 6px 18px -4px rgba(239,43,56,.55); }
.ai-bot-pulse { position: absolute; inset: -3px; border-radius: 16px; border: 1.5px solid rgba(250,150,155,.6); animation: pulse 2.4s ease-out infinite; }
@keyframes pulse { 0% { transform: scale(1); opacity: .8; } 100% { transform: scale(1.25); opacity: 0; } }
.ai-bot-id { display: flex; flex-direction: column; gap: 3px; }
.ai-bot-name { font-size: 15px; font-weight: 800; letter-spacing: -.01em; color: #fff; }
.ai-bot-status { font-size: 11.5px; color: #f0b8bc; display: inline-flex; align-items: center; gap: 6px; font-weight: 500; }
.ai-bot-status i { width: 7px; height: 7px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 3px rgba(52,211,153,.22); display: inline-block; }
.ai-console.idle .ai-bot-status i { background: #f0b8bc; box-shadow: 0 0 0 3px rgba(240,184,188,.22); }

.ai-console-story { position: relative; font-size: 17px; line-height: 1.55; color: #f3dcdd; letter-spacing: -.005em; max-width: 60ch; }
.ai-console-story b { color: #fff; font-weight: 800; }
.ai-console-story .pos { color: #6ee7b7; }
.ai-console-story .neg { color: #fcd34d; }
.ai-console-roi { position: relative; margin-top: 14px; display: inline-flex; align-items: center; gap: 10px; font-size: 12.5px; color: #f0b8bc; }
.ai-roi-badge { background: linear-gradient(135deg, #34d399, #059669); color: #04160f; font-weight: 800; font-size: 13px; padding: 4px 11px; border-radius: 8px; letter-spacing: -.01em; }

/* stat cards grid */
.ai-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(158px, 1fr)); gap: 13px; margin-bottom: 20px; }

/* precision meter */
.ai-meter { background: var(--card); border: 1px solid var(--card-bd); border-radius: 14px; padding: 16px 18px; margin-bottom: 8px; }
.ai-meter-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px; }
.ai-meter-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); }
.ai-meter-pct { font-size: 24px; font-weight: 800; color: var(--pos); line-height: 1; letter-spacing: -.02em; }
.ai-meter-bar { height: 8px; border-radius: 20px; background: var(--track); overflow: hidden; }
.ai-meter-fill { height: 100%; border-radius: 20px; background: linear-gradient(90deg, #34d399, #059669); transition: width .5s cubic-bezier(.4,0,.2,1); }
.ai-meter-foot { margin-top: 10px; font-size: 12.5px; color: var(--ink-soft); line-height: 1.5; }
.ai-meter-foot b { color: var(--ink); font-weight: 700; }
.ai-meter-foot .dim { color: var(--muted); }

/* per-seller table */
.ai-section { margin-top: 24px; }
.ai-section-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.ai-section h2 { font-size: 15px; font-weight: 800; margin: 0; color: var(--ink); letter-spacing: -.01em; }
.ai-section-count { font-size: 11px; font-weight: 600; color: var(--muted); background: var(--track); border-radius: 20px; padding: 2px 9px; }
/* seller search */
.ai-search { margin-left: auto; min-width: 200px; max-width: 260px; padding: 7px 11px; font-size: 12.5px;
  border: 1px solid var(--card-bd); border-radius: 9px; background: var(--card); color: inherit; outline: none; }
.ai-search:focus { border-color: #94a3b8; box-shadow: 0 0 0 3px rgba(148,163,184,.18); }
/* headline — the whole story in one sentence, before any numbers */
.ai-lede { margin: 0 0 16px; }
.ai-lede-main { margin: 0; font-size: 16px; line-height: 1.55; color: var(--ink); }
.ai-lede-main b { font-weight: 800; }
.ai-lede-sub { margin: 6px 0 0; font-size: 13px; line-height: 1.6; color: var(--muted); }
.ai-lede-sub b { color: var(--ink-soft); font-weight: 700; }
/* order value — one quiet line, not a panel */
.ai-value { display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  font-size: 12.5px; color: var(--muted); margin: 10px 0 20px; }
.ai-value b { color: var(--ink); font-weight: 800; font-variant-numeric: tabular-nums; }
.ai-value-sep { opacity: .45; }
.ai-table-wrap { background: var(--card); border: 1px solid var(--card-bd); border-radius: 14px; overflow-x: auto; }
/* 7 columns now (address quality / value / confidence added) — keep them from
   crushing; .ai-table-wrap scrolls horizontally on narrow screens. */
.ai-tbl { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 860px; }
.ai-tbl th { text-align: left; color: var(--muted); font-weight: 700; font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; padding: 12px 16px; background: var(--track); border-bottom: 1px solid var(--card-bd); }
.ai-tbl td { padding: 13px 16px; border-bottom: 1px solid var(--card-bd); font-variant-numeric: tabular-nums; }
.ai-tbl tr:last-child td { border-bottom: 0; }
.ai-tbl tbody tr { transition: background .14s; }
.ai-tbl tbody tr:hover td { background: rgba(225,29,42,.035); }
.ta-r { text-align: right; }
.ai-num { color: var(--ink-soft); font-weight: 600; }
.ai-seller { display: inline-flex; align-items: center; gap: 10px; max-width: 100%; }
.ai-seller-dot { width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; color: #fff; background: linear-gradient(140deg, #ef2b38, #a81119); }
.ai-seller-name { font-weight: 700; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }
.ai-fee-cell { font-weight: 800; color: var(--neg); }
/* name + share bar stacked */
.ai-seller-col { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.ai-share { display: block; height: 3px; border-radius: 3px; background: var(--track); overflow: hidden; }
.ai-share i { display: block; height: 100%; border-radius: 3px; background: linear-gradient(90deg, #ef2b38, #f87171); }
/* a zero is information, not an alarm — mute it instead of shouting in red */
.ai-tbl td.ai-zero { color: var(--muted); font-weight: 600; opacity: .75; }
.ai-num b { font-weight: 800; color: var(--ink); }
.ai-empty-row { text-align: center; color: var(--muted); font-size: 12.5px; padding: 22px 16px !important; }
/* address quality per seller — the column that actually varies */
.ta-c { text-align: center; }
.ai-addr-pill { display: inline-block; min-width: 46px; padding: 3px 9px; border-radius: 20px;
  font-size: 12px; font-weight: 800; font-variant-numeric: tabular-nums; }
.ai-addr-pill.rate-good { background: #f0fdf4; border: 1px solid #dcfce7; color: #15803d; }
.ai-addr-pill.rate-mid  { background: #fffbeb; border: 1px solid #fde68a; color: #b45309; }
.ai-addr-pill.rate-bad  { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.ai-addr-sub { display: block; margin-top: 4px; font-size: 10.5px; color: var(--muted); white-space: nowrap; }

/* ── orders table ── */
/* Was min-width:980px, which pinned the table wider than the popup body so it
   ran into both borders whatever padding .ai-modal had. 720px still keeps the
   9 columns readable and lets the frame sit inside the padding; the wrapper
   scrolls horizontally when the viewport really is narrow. */
.ai-tbl-orders { min-width: 720px; }
/* scroll in a fixed frame, header pinned, with a visible scrollbar */
.ai-scroll { max-height: 62vh; overflow: auto; overscroll-behavior: contain; }
.ai-scroll thead th { position: sticky; top: 0; z-index: 2; }
.ai-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ai-scroll::-webkit-scrollbar-track { background: var(--track); }
.ai-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; border: 2px solid var(--track); }
.ai-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
/* shipping-status dropdown — built in CSS so the caret and the open list match the
   rest of the page instead of the browser's native select chrome. */
.ai-dd { position: relative; margin-left: auto; }
.ai-dd-btn { display: flex; align-items: center; gap: 8px; min-width: 175px;
  padding: 7px 10px; font: inherit; font-size: 12.5px; text-align: left;
  border: 1px solid var(--card-bd); border-radius: 9px; background: var(--card);
  color: inherit; cursor: pointer; }
.ai-dd-btn:hover { border-color: #94a3b8; }
.ai-dd.open .ai-dd-btn { border-color: #94a3b8; box-shadow: 0 0 0 3px rgba(148,163,184,.18); }
.ai-dd-caret { margin-left: auto; flex: none; color: var(--muted); transition: transform .15s ease; }
.ai-dd.open .ai-dd-caret { transform: rotate(180deg); }
.ai-dd-any { color: var(--muted); }
/* sits under the menu and over everything else, so any outside click closes it */
.ai-dd-back { position: fixed; inset: 0; z-index: 40; }
.ai-dd-menu { position: absolute; z-index: 41; top: calc(100% + 6px); right: 0;
  min-width: 100%; max-height: 260px; overflow-y: auto; padding: 4px;
  border: 1px solid var(--card-bd); border-radius: 10px; background: var(--card);
  box-shadow: 0 10px 28px rgba(15,23,42,.14); }
.ai-dd-item { display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 6px 8px; font: inherit; font-size: 12.5px; text-align: left;
  border: 0; border-radius: 7px; background: none; color: inherit; cursor: pointer; }
.ai-dd-item:hover { background: var(--track); }
.ai-dd-item.sel { background: var(--track); font-weight: 600; }
.ai-dd-n { margin-left: auto; font-size: 11.5px; color: var(--muted);
  font-variant-numeric: tabular-nums; }
/* call-centre note — clamped to two lines so one long remark cannot stretch the row */
.ai-comment { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden; max-width: 210px; font-size: 12px; line-height: 1.35; color: var(--ink-soft); }
.ai-ord-ref { font-weight: 700; color: var(--ink); }
.ai-dim { color: var(--muted); }
.ai-dim.sm { font-size: 11.5px; }
.block { display: block; }
.nowrap { white-space: nowrap; }
.ai-track { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 11.5px;
  background: var(--track); border: 1px solid var(--card-bd); border-radius: 6px; padding: 2px 7px; white-space: nowrap; }
.ai-addr { display: inline-block; max-width: 230px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 12.5px; padding: 2px 8px; border-radius: 7px; }
.ai-addr.ok  { background: #f0fdf4; border: 1px solid #dcfce7; color: #166534; }
.ai-addr.bad { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
/* city-only: shippable, so amber (a caution) rather than red (a blocker) */
.ai-addr.city { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.ai-status-pill { display: inline-block; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 700; white-space: nowrap; }
.ai-status-pill.st-good { background: #f0fdf4; border: 1px solid #dcfce7; color: #15803d; }
.ai-status-pill.st-mid  { background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8; }
.ai-status-pill.st-bad  { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
/* tighter pill for the dropdown button and its menu rows */
.ai-status-pill.sm { padding: 2px 7px; font-size: 11px; }

/* ── eye button + per-seller popup ── */
.ai-eye { display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; cursor: pointer;
  background: var(--track); border: 1px solid var(--card-bd); color: var(--muted);
  transition: background .14s, color .14s, border-color .14s; }
.ai-eye:hover { background: #fee2e2; border-color: #fecaca; color: #dc2626; }
.ai-eye:focus-visible { outline: 2px solid #94a3b8; outline-offset: 2px; }
.ai-modal { padding: 8px 24px 24px; }
/* on a phone the popup is nearly full-width, so give the edges less away */
@media (max-width: 640px) { .ai-modal { padding: 6px 14px 16px; } }
.ai-modal-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.ai-modal-title { min-width: 0; }
.ai-modal-title h3 { margin: 0; font-size: 17px; font-weight: 800; color: var(--ink); }
.ai-modal-title p { margin: 3px 0 0; font-size: 12.5px; color: var(--muted); }
.ai-modal-title p b { color: var(--ink-soft); font-weight: 700; }
.ai-seller-dot.lg { width: 38px; height: 38px; border-radius: 11px; font-size: 16px; }
.ai-modal-head .ai-search { margin-left: auto; }
/* the popup already scrolls its own body — keep the table frame shorter */
.ai-modal-scroll { max-height: 56vh; }

/* per-seller pagination */
.ai-pager { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 14px; }
.ai-pager-btn { font-size: 12.5px; font-weight: 600; color: var(--ink-soft); background: var(--card);
  border: 1px solid var(--card-bd); border-radius: 9px; padding: 7px 14px; cursor: pointer; transition: all .15s; }
.ai-pager-btn:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); }
.ai-pager-btn:disabled { opacity: .45; cursor: not-allowed; }
.ai-pager-info { font-size: 12.5px; color: var(--ink-soft); }
.ai-pager-info b { color: var(--ink); }
.ai-pager-dim { color: var(--muted); }

/* empty state */
.ai-empty { display: flex; align-items: center; gap: 16px; padding: 24px; background: var(--card); border: 1px dashed var(--card-bd); border-radius: 16px; margin-top: 16px; }
.ai-empty-icon { font-size: 32px; opacity: .7; }
.ai-empty-title { font-size: 15px; font-weight: 800; color: var(--ink); }
.ai-empty-sub { font-size: 13px; color: var(--ink-soft); margin-top: 3px; }

/* Light-only: the app has no dark theme, so we don't follow OS dark preference.
   The red console hero is intentionally dark (a device, matching the app's red
   primary), everything else stays light. */
</style>
